import type {
  SocialDateRange,
  SocialGbpKeyword,
  SocialGoogleBusiness,
  SocialMetricSeries,
} from '../../types/socialReporting.js';
import { sumSeriesInRange } from './socialSeries.js';
import { periodDelta } from './socialOverview.js';

// Google Business reporting — pure performance aggregation.
//
// No I/O, no Date.now (dates are injected). The server fetches Zernio's
// getGoogleBusinessPerformance envelope (identical { total, values:[{date,value}] }
// shape as the FB/IG insights envelopes, so socialSeries' readers work on it too);
// everything about how the metrics roll up into KPIs, how the daily lines are
// assembled, and how the keyword list is ranked lives HERE so it's unit-testable
// off the observed fixtures (observed-2026-07-23).
//
// ═══ THE AUTHORITATIVE METRIC LIST (probe Q4, 2026-07-23) ═══
// The server's own 400-error `validMetrics` enumerated these 11 — TRUSTED over the
// SDK doc's hint (fixtures rule). Google splits impressions four ways
// (desktop/mobile × maps/search); the lead-adjacent actions are calls / direction
// requests / website clicks; bookings / conversations / food are real-zero for
// businesses that don't use those features.
//
// ═══ THE NON-ZERO GATE THIS MODULE ENFORCES (mutation-tested) ═══
// The OPTIONAL action metrics (bookings / conversations / food) render ONLY when
// non-zero in the period — a genuine "the business doesn't use that feature", NOT
// "not measurable". They are NOT hardcoded out (they may be real for other
// verticals); `nonZeroOptionalActions` is the single gate, and removing its filter
// must fail a test. The four impression splits + the three headline actions are
// always shown (a measured 0 call-click is still measurable, unlike GBP under the
// old not-measurable gate).

// ---------------------------------------------------------------------------
// the metric catalogue (shared by the server request + the client grouping)
// ---------------------------------------------------------------------------

export const GBP_IMPRESSION_METRICS = [
  'BUSINESS_IMPRESSIONS_DESKTOP_MAPS',
  'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH',
  'BUSINESS_IMPRESSIONS_MOBILE_MAPS',
  'BUSINESS_IMPRESSIONS_MOBILE_SEARCH',
] as const;

// The lead-adjacent headline actions — always shown (a measured 0 is measurable).
export const GBP_HEADLINE_ACTION_METRICS = [
  'CALL_CLICKS',
  'BUSINESS_DIRECTION_REQUESTS',
  'WEBSITE_CLICKS',
] as const;

// Optional actions — shown only when non-zero (the mutation-tested gate).
export const GBP_OPTIONAL_ACTION_METRICS = [
  'BUSINESS_BOOKINGS',
  'BUSINESS_CONVERSATIONS',
  'BUSINESS_FOOD_ORDERS',
  'BUSINESS_FOOD_MENU_CLICKS',
] as const;

// The authoritative 11 (probe Q4) — the exact set the server requests.
export const GBP_ALL_METRICS: string[] = [
  ...GBP_IMPRESSION_METRICS,
  ...GBP_HEADLINE_ACTION_METRICS,
  ...GBP_OPTIONAL_ACTION_METRICS,
];

export const GBP_METRIC_LABEL: Record<string, string> = {
  BUSINESS_IMPRESSIONS_DESKTOP_MAPS: 'Desktop Maps',
  BUSINESS_IMPRESSIONS_DESKTOP_SEARCH: 'Desktop Search',
  BUSINESS_IMPRESSIONS_MOBILE_MAPS: 'Mobile Maps',
  BUSINESS_IMPRESSIONS_MOBILE_SEARCH: 'Mobile Search',
  CALL_CLICKS: 'Calls',
  BUSINESS_DIRECTION_REQUESTS: 'Direction requests',
  WEBSITE_CLICKS: 'Website clicks',
  BUSINESS_BOOKINGS: 'Bookings',
  BUSINESS_CONVERSATIONS: 'Messages',
  BUSINESS_FOOD_ORDERS: 'Food orders',
  BUSINESS_FOOD_MENU_CLICKS: 'Menu clicks',
};

/** A metric's display label, falling back to the raw name for an unknown key. */
export function gbpMetricLabel(metric: string): string {
  return GBP_METRIC_LABEL[metric] ?? metric;
}

// ---------------------------------------------------------------------------
// small helpers
// ---------------------------------------------------------------------------

function seriesOf(data: SocialGoogleBusiness, metric: string): SocialMetricSeries | null {
  return data.metrics[metric] ?? null;
}

/** Sum a metric's daily values within a window (0 when absent), from a metrics map. */
function mapMetricSum(
  metrics: Record<string, SocialMetricSeries>,
  metric: string,
  range: SocialDateRange,
): number {
  return sumSeriesInRange(metrics[metric] ?? null, range);
}

/** Sum a set of metrics within a window, from a metrics map. */
function mapMetricSetSum(
  metrics: Record<string, SocialMetricSeries>,
  metricKeys: readonly string[],
  range: SocialDateRange,
): number {
  let sum = 0;
  for (const m of metricKeys) sum += mapMetricSum(metrics, m, range);
  return sum;
}

/** Sum a metric's daily values within a window (0 when absent). */
function metricSum(data: SocialGoogleBusiness, metric: string, range: SocialDateRange): number {
  return mapMetricSum(data.metrics, metric, range);
}

// ---------------------------------------------------------------------------
// KPI cards
// ---------------------------------------------------------------------------

export interface GbpKpi {
  key: string;
  label: string;
  current: number;
  previous: number;
  deltaPct: number | null;
  direction: 'up' | 'down' | 'flat' | null;
}

function kpi(key: string, label: string, current: number, previous: number): GbpKpi {
  const { deltaPct, direction } = periodDelta(current, previous);
  return { key, label, current, previous, deltaPct, direction };
}

/**
 * The headline KPIs from a metrics map + two windows: total impressions (sum of
 * the four splits) + the three lead-adjacent actions (calls / direction requests /
 * website clicks), each with its period-over-period delta. Reused by the Overview
 * GBP mini-card (which holds only the metrics map, not a full SocialGoogleBusiness).
 */
export function buildGbpHeadlineKpis(
  metrics: Record<string, SocialMetricSeries>,
  range: SocialDateRange,
  previousRange: SocialDateRange,
): GbpKpi[] {
  const kpis: GbpKpi[] = [
    kpi(
      'total_impressions',
      'Total impressions',
      mapMetricSetSum(metrics, GBP_IMPRESSION_METRICS, range),
      mapMetricSetSum(metrics, GBP_IMPRESSION_METRICS, previousRange),
    ),
  ];
  for (const m of GBP_HEADLINE_ACTION_METRICS) {
    kpis.push(
      kpi(m, gbpMetricLabel(m), mapMetricSum(metrics, m, range), mapMetricSum(metrics, m, previousRange)),
    );
  }
  return kpis;
}

/**
 * The headline KPI row: total impressions + calls / directions / website clicks,
 * each with its period-over-period delta. These four are always present — a
 * measured 0 is a real, measurable figure.
 */
export function buildGbpKpis(data: SocialGoogleBusiness): GbpKpi[] {
  return buildGbpHeadlineKpis(data.metrics, data.range, data.previousRange);
}

/**
 * The optional action metric KEYS that carry a NON-ZERO total in the window — the
 * single gate `nonZeroOptionalActions` and the action charts both read, so the
 * tiles and the chart agree on which optional actions exist. Shared with the
 * monthly view (which passes a metrics map + a 12-month window).
 */
export function nonZeroOptionalActionKeys(
  metrics: Record<string, SocialMetricSeries>,
  range: SocialDateRange,
): string[] {
  return GBP_OPTIONAL_ACTION_METRICS.filter(
    (m) => sumSeriesInRange(metrics[m] ?? null, range) > 0,
  );
}

/**
 * ⚠️ THE NON-ZERO GATE (mutation-tested). The optional action metrics
 * (bookings / conversations / food) that carry a NON-ZERO total in the CURRENT
 * period — each as a KPI-style stat with its delta. A metric that's zero this
 * period is a real "the business doesn't use that feature" and is omitted, NOT
 * rendered as 0 or as "not measurable". Removing the gate must fail a test.
 */
export function nonZeroOptionalActions(data: SocialGoogleBusiness): GbpKpi[] {
  return nonZeroOptionalActionKeys(data.metrics, data.range).map((m) =>
    kpi(m, gbpMetricLabel(m), metricSum(data, m, data.range), metricSum(data, m, data.previousRange)),
  );
}

// ---------------------------------------------------------------------------
// chart rows — daily and monthly (the granularity toggle)
// ---------------------------------------------------------------------------

export interface GbpDailyRow {
  date: string;
  [metric: string]: number | string;
}

export interface GbpMonthlyRow {
  month: string; // YYYY-MM
  [metric: string]: number | string;
}

/**
 * Build date-keyed rows for a set of metrics over a window, from a metrics map.
 * The row date set is the union of every selected metric's in-range dates; a
 * metric with no point on a given day contributes 0 there (a GBP daily count of 0
 * is a real zero, unlike a follower snapshot gap). Date-sorted for the x-axis.
 */
export function buildDailyRowsFromMetrics(
  metrics: Record<string, SocialMetricSeries>,
  metricKeys: readonly string[],
  range: SocialDateRange,
): GbpDailyRow[] {
  const byDate = new Map<string, GbpDailyRow>();
  for (const metric of metricKeys) {
    const series = metrics[metric];
    if (!series) continue;
    for (const point of series.values) {
      if (point.date < range.from || point.date > range.to) continue;
      const row = byDate.get(point.date) ?? { date: point.date };
      row[metric] = ((row[metric] as number | undefined) ?? 0) + point.value;
      byDate.set(point.date, row);
    }
  }
  for (const row of byDate.values()) {
    for (const metric of metricKeys) if (row[metric] === undefined) row[metric] = 0;
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Roll a set of metrics' daily values up into CALENDAR-MONTH buckets over a
 * window, for the 12-month monthly view — each bucket is the sum of that metric's
 * daily values whose day falls in the month. A metric with no data in a present
 * month contributes 0 there. Month-sorted (YYYY-MM lexical = chronological).
 */
export function buildMonthlyRowsFromMetrics(
  metrics: Record<string, SocialMetricSeries>,
  metricKeys: readonly string[],
  range: SocialDateRange,
): GbpMonthlyRow[] {
  const byMonth = new Map<string, GbpMonthlyRow>();
  for (const metric of metricKeys) {
    const series = metrics[metric];
    if (!series) continue;
    for (const point of series.values) {
      if (point.date < range.from || point.date > range.to) continue;
      const month = point.date.slice(0, 7); // YYYY-MM
      const row = byMonth.get(month) ?? { month };
      row[metric] = ((row[metric] as number | undefined) ?? 0) + point.value;
      byMonth.set(month, row);
    }
  }
  for (const row of byMonth.values()) {
    for (const metric of metricKeys) if (row[metric] === undefined) row[metric] = 0;
  }
  return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
}

/**
 * The 12-month monthly window ending at the report period's end — the first day
 * of the month 11 calendar months back, through `rangeTo`. Deterministic (derived
 * from the selected period, no clock read) so it's a stable query key. Google's
 * 18-month floor comfortably contains it; the server clamps defensively anyway.
 */
export function twelveMonthWindow(rangeTo: string): SocialDateRange {
  const to = new Date(`${rangeTo}T00:00:00Z`);
  const from = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth() - 11, 1));
  return { from: from.toISOString().slice(0, 10), to: rangeTo };
}

/** The four impression splits as daily rows over the current period. */
export function buildImpressionDailyRows(data: SocialGoogleBusiness): GbpDailyRow[] {
  return buildDailyRowsFromMetrics(data.metrics, GBP_IMPRESSION_METRICS, data.range);
}

/**
 * The action metrics as daily rows over the current period: the three headline
 * actions plus any non-zero optional action (same gate as the KPI row, so the
 * chart and the tiles agree on which optional actions exist).
 */
export function buildActionDailyRows(data: SocialGoogleBusiness): {
  rows: GbpDailyRow[];
  metrics: string[];
} {
  const metrics = actionMetricKeys(data.metrics, data.range);
  return { rows: buildDailyRowsFromMetrics(data.metrics, metrics, data.range), metrics };
}

/**
 * The action metrics to chart over a window: the three headline actions plus any
 * non-zero optional action in that window. Shared by the daily and monthly views
 * so both pick the same set (and the tiles agree).
 */
export function actionMetricKeys(
  metrics: Record<string, SocialMetricSeries>,
  range: SocialDateRange,
): string[] {
  return [...GBP_HEADLINE_ACTION_METRICS, ...nonZeroOptionalActionKeys(metrics, range)];
}

// ---------------------------------------------------------------------------
// "has any performance data" gate
// ---------------------------------------------------------------------------

/**
 * Whether the account has ANY GBP performance data in the current period (any of
 * the 11 metrics has a non-zero in-range total). Drives the tab's performance
 * empty state — distinct from `connected` (a just-connected account can be
 * connected but have no data yet).
 */
export function hasGbpPerformanceData(data: SocialGoogleBusiness): boolean {
  return GBP_ALL_METRICS.some((m) => metricSum(data, m, data.range) > 0);
}

// ---------------------------------------------------------------------------
// search keywords
// ---------------------------------------------------------------------------

/** Rank keywords by impressions descending, keeping the top `limit`. Pure. */
export function rankGbpKeywords(
  keywords: SocialGbpKeyword[],
  limit = 25,
): SocialGbpKeyword[] {
  return [...keywords].sort((a, b) => b.impressions - a.impressions).slice(0, limit);
}

// ---------------------------------------------------------------------------
// top-level view
// ---------------------------------------------------------------------------

export interface GbpPerformanceView {
  kpis: GbpKpi[];
  optionalActions: GbpKpi[];
  impressionRows: GbpDailyRow[];
  actionChart: { rows: GbpDailyRow[]; metrics: string[] };
  keywords: SocialGbpKeyword[];
  hasPerformanceData: boolean;
}

export function buildGbpPerformanceView(data: SocialGoogleBusiness): GbpPerformanceView {
  return {
    kpis: buildGbpKpis(data),
    optionalActions: nonZeroOptionalActions(data),
    impressionRows: buildImpressionDailyRows(data),
    actionChart: buildActionDailyRows(data),
    keywords: rankGbpKeywords(data.keywords),
    hasPerformanceData: hasGbpPerformanceData(data),
  };
}

// ---------------------------------------------------------------------------
// display formatting
// ---------------------------------------------------------------------------

/** Format a period-over-period delta (e.g. "+12.4%"); null when incomparable. */
export function formatGbpDelta(kpi: GbpKpi): string | null {
  if (kpi.deltaPct === null) return null;
  const sign = kpi.deltaPct > 0 ? '+' : '';
  return `${sign}${kpi.deltaPct.toFixed(1)}%`;
}
